import { Component, OnInit } from '@angular/core';
import { NgClass, DecimalPipe, SlicePipe } from '@angular/common';
import { Router } from '@angular/router';
import { TracerService } from '../../../services/tracer.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import {
  REPORT_PERIODS,
  REPORT_DIMENSIONS,
  STATUS_COLORS,
  ReportPeriod,
  ReportDimension,
  ReportSummaryResponse,
  ReportBreakdownResponse,
  ReportGrain,
} from '../../../models/reports.model';

// Errors are no longer a standalone metric — they are the bottom band of
// a stacked area, so the error proportion within the total reads at a
// glance.
type Metric = 'requests' | 'sessions';

const ERROR_COLOR = '#f59e0b';

interface ChartPoint {
  x: number;
  y: number;
  value: number;
  label: string;
  rate?: number; // error rate (%) for the error band tooltip
}
interface GridLine {
  y: number;
  label: string;
}
interface ChartModel {
  color: string; // primary metric colour
  errorColor: string;
  // Stacked area: errorAreaPath (bottom, amber) + restAreaPath (top,
  // primary) sum to the selected metric total. The two top edges are
  // stroked for crispness.
  errorAreaPath: string;
  restAreaPath: string;
  totalLinePath: string;
  errorLinePath: string;
  points: ChartPoint[]; // total (top line) — tooltips
  errorPoints: ChartPoint[]; // error band top — tooltips
  grid: GridLine[];
  xlabels: { x: number; label: string }[];
}

// Admin-only Reports. Reads pre-aggregated rollups via TracerService;
// the backend is the real gate (admin-only), this component soft-bounces
// non-admins like the Settings shell does. Chart is inline SVG (no chart
// dependency) — see reports-mockup.html for the visual reference.
@Component({
  selector: 'app-reports',
  imports: [NgClass, DecimalPipe, SlicePipe],
  templateUrl: './reports.html',
})
export class Reports implements OnInit {
  readonly periods = REPORT_PERIODS;
  readonly periodGroups: ReportPeriod['group'][] = ['Hours', 'Days', 'Months'];
  readonly dimensions = REPORT_DIMENSIONS;

  selectedPeriodKey = '7d';
  metric: Metric = 'requests';
  dimension: ReportDimension = 'application';

  fromIso = '';
  toIso = '';

  summary: ReportSummaryResponse | null = null;
  breakdown: ReportBreakdownResponse | null = null;

  loadingSummary = false;
  loadingBreakdown = false;
  error: string | null = null;

  // Fixed SVG canvas; scaled to container width via CSS.
  private readonly W = 1000;
  private readonly H = 240;
  private readonly padL = 48;
  private readonly padR = 14;
  private readonly padT = 14;
  private readonly padB = 28;

  constructor(
    private tracer: TracerService,
    private ls: LocalStorageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.ls.getItem('agRealm') !== 'admins') {
      this.router.navigateByUrl('/');
      return;
    }
    this.selectPeriod(this.selectedPeriodKey);
  }

  periodsInGroup(group: ReportPeriod['group']): ReportPeriod[] {
    return this.periods.filter(p => p.group === group);
  }

  selectPeriod(key: string): void {
    const period = this.periods.find(p => p.key === key);
    if (!period) return;
    this.selectedPeriodKey = key;
    const to = new Date();
    const from = new Date(to.getTime() - period.hours * 3600 * 1000);
    this.toIso = to.toISOString();
    this.fromIso = from.toISOString();
    this.loadAll();
  }

  setMetric(m: Metric): void {
    this.metric = m;
  }

  setDimension(d: ReportDimension): void {
    this.dimension = d;
    this.loadBreakdown();
  }

  private loadAll(): void {
    this.error = null;
    this.loadingSummary = true;
    this.tracer.getReportSummary(this.fromIso, this.toIso).subscribe({
      next: res => {
        this.summary = res;
        this.loadingSummary = false;
      },
      error: () => {
        this.error = 'Failed to load report summary.';
        this.loadingSummary = false;
      },
    });
    this.loadBreakdown();
  }

  private loadBreakdown(): void {
    this.loadingBreakdown = true;
    this.tracer.getReportBreakdown(this.fromIso, this.toIso, this.dimension).subscribe({
      next: res => {
        this.breakdown = res;
        this.loadingBreakdown = false;
      },
      error: () => {
        this.loadingBreakdown = false;
      },
    });
  }

  // ---- KPIs -------------------------------------------------------------
  get totals() {
    return this.summary?.totals ?? null;
  }

  get errorRatePct(): number {
    return (this.summary?.totals.error_rate ?? 0) * 100;
  }

  // ---- chart ------------------------------------------------------------
  readonly errorColor = ERROR_COLOR;

  get metricColor(): string {
    return this.metric === 'sessions' ? '#2563eb' : '#cd0005';
  }

  metricValue(p: { requests: number; sessions: number }): number {
    return this.metric === 'sessions' ? p.sessions : p.requests;
  }

  get metricLabel(): string {
    return this.metric === 'sessions' ? 'Sessions' : 'Requests';
  }

  get hasData(): boolean {
    return !!this.summary && this.summary.series.length > 0;
  }

  get chart(): ChartModel | null {
    const s = this.summary;
    if (!s || s.series.length === 0) return null;
    const n = s.series.length;
    const iw = this.W - this.padL - this.padR;
    const ih = this.H - this.padT - this.padB;
    const yBase = this.padT + ih;
    const x = (i: number) => this.padL + (n === 1 ? iw / 2 : (iw * i) / (n - 1));

    // Stacked area on a single (count) axis: the error band sits at the
    // bottom, the rest of the selected metric stacks on top, so the two
    // sum to the metric total and the error share reads at a glance.
    // Errors are request-level; the band is clamped to the metric total
    // so a sessions bucket with more request-errors than sessions can't
    // produce a negative top band.
    const totalData = s.series.map(p => this.metricValue(p));
    const errData = s.series.map((p, i) => Math.min(p.errors, totalData[i]));
    const max = Math.max(1, ...totalData) * 1.12;
    const y = (v: number) => this.padT + ih - (v / max) * ih;

    const grid: GridLine[] = [];
    for (let g = 0; g <= 4; g++) {
      grid.push({ y: this.padT + (ih * g) / 4, label: this.fmtCompact(max * (1 - g / 4)) });
    }

    const points: ChartPoint[] = s.series.map((p, i) => ({
      x: x(i),
      y: y(totalData[i]),
      value: totalData[i],
      label: this.fmtBucket(p.bucket, s.grain),
    }));
    const errorPoints: ChartPoint[] = s.series.map((p, i) => ({
      x: x(i),
      y: y(errData[i]),
      value: p.errors,
      label: this.fmtBucket(p.bucket, s.grain),
      rate: p.requests > 0 ? (p.errors / p.requests) * 100 : 0,
    }));

    const toLine = (pts: ChartPoint[]) =>
      pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
    const totalLinePath = toLine(points);
    const errorLinePath = toLine(errorPoints);

    // error band: baseline → error top edge → back to baseline
    const errorAreaPath =
      `M${x(0).toFixed(1)},${yBase.toFixed(1)} ` +
      errorPoints.map(p => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') +
      ` L${x(n - 1).toFixed(1)},${yBase.toFixed(1)} Z`;
    // rest band: along total top edge forward, then back along error edge
    const restAreaPath =
      `M${points.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' L')} ` +
      `L${errorPoints
        .slice()
        .reverse()
        .map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
        .join(' L')} Z`;

    const every = n <= 16 ? 1 : Math.ceil(n / 12);
    const xlabels = points
      .map((p, i) => ({ x: p.x, label: p.label, i }))
      .filter(o => o.i % every === 0)
      .map(o => ({ x: o.x, label: o.label }));

    return {
      color: this.metricColor,
      errorColor: ERROR_COLOR,
      errorAreaPath,
      restAreaPath,
      totalLinePath,
      errorLinePath,
      points,
      errorPoints,
      grid,
      xlabels,
    };
  }

  get gridBottom(): number {
    return this.H - this.padB;
  }
  get plotLeft(): number {
    return this.padL;
  }
  get plotRight(): number {
    return this.W - this.padR;
  }
  get viewBox(): string {
    return `0 0 ${this.W} ${this.H}`;
  }

  // ---- status / outcomes ------------------------------------------------
  get statusTotal(): number {
    return (this.summary?.status ?? []).reduce((a, s) => a + s.count, 0);
  }

  statusColor(status: string): string {
    return STATUS_COLORS[status] ?? '#9ca3af';
  }

  statusShare(count: number): number {
    const t = this.statusTotal;
    return t > 0 ? count / t : 0;
  }

  statusLabel(status: string): string {
    switch (status) {
      case 'ok':
        return 'OK';
      case 'warning':
        return 'Warning';
      case 'error':
        return 'Error';
      case 'not_set':
        return 'Not set';
      default:
        return status;
    }
  }

  // Donut: cumulative dash offsets over a fixed circumference.
  readonly donutR = 44;
  readonly donutC = 2 * Math.PI * 44;
  donutSegments(): { color: string; dash: string; offset: number }[] {
    let acc = 0;
    return (this.summary?.status ?? []).map(s => {
      const len = this.statusShare(s.count) * this.donutC;
      const seg = {
        color: this.statusColor(s.status),
        dash: `${len} ${this.donutC - len}`,
        offset: -acc,
      };
      acc += len;
      return seg;
    });
  }

  get okPct(): number {
    const ok = (this.summary?.status ?? []).find(s => s.status === 'ok');
    return ok ? this.statusShare(ok.count) * 100 : 0;
  }

  // ---- formatting -------------------------------------------------------
  fmtCompact(n: number): string {
    if (n >= 1e6) return (n / 1e6).toFixed(n >= 1e7 ? 0 : 1) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(n >= 1e4 ? 0 : 1) + 'k';
    return `${Math.round(n)}`;
  }

  /** Error-rate axis/label formatter: 1 decimal under 10%, else whole. */
  fmtPct(n: number): string {
    return (n >= 10 ? Math.round(n).toString() : n.toFixed(1)) + '%';
  }

  private readonly MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  fmtBucket(iso: string, grain: ReportGrain): string {
    const d = new Date(iso);
    if (grain === 'hour') {
      return `${String(d.getUTCHours()).padStart(2, '0')}:00`;
    }
    if (grain === 'day') {
      return `${this.MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
    }
    return `${this.MONTHS[d.getUTCMonth()]} '${String(d.getUTCFullYear()).slice(2)}`;
  }

  grainLabel(): string {
    switch (this.summary?.grain) {
      case 'hour':
        return 'hourly buckets';
      case 'day':
        return 'daily buckets';
      case 'month':
        return 'monthly buckets · macro rollup';
      default:
        return '';
    }
  }
}
